/**
 * `<HeldResolveDiscard/>` — #2263, the one-press way out of the held
 * re-solve's refusal.
 *
 * Render it beside any error a cover-family write can show (the ladder, the
 * cover change, standby, the MC ask, the held-swap confirm): it draws nothing
 * unless that error is the held re-solve's 409 (`isHeldResolveRefusal`). Then
 * it offers **Discard the held re-solve**; pressed, the window's held drafts
 * and its list go, every read refreshes, and the line says the roster that is
 * out stands and to try the cover again. With `always`, it draws the button
 * regardless (the roster page's held re-solve line).
 */
import { useEffect, useState } from "react";

import { ApiError } from "../api/client";
import { useDiscardHeldResolve } from "../api/useDiscardHeldResolve";
import {
  DISCARD_HELD_RESOLVE_LABEL,
  NOTHING_HELD_LINE,
  heldResolveDiscardedLine,
  isHeldResolveRefusal,
} from "../lib/heldResolve";
import { Button } from "./ui";

interface HeldResolveDiscardProps {
  /** The window the refused write was on. */
  cycleId: string;
  /** The error the surface shows (an `ApiError` or its text). */
  error?: unknown;
  /** Show the button without a refusal (the held re-solve's own line). */
  always?: boolean;
  /** The line's last sentence after a discard (default: try the cover again). */
  then?: string;
  testid?: string;
  /** Called with the line shown after the discard (or the 404's line). */
  onDiscarded?: (line: string) => void;
}

export function HeldResolveDiscard(props: HeldResolveDiscardProps) {
  // Nothing (and no query client asked for) unless the button is wanted:
  // the surfaces that render this beside every error stay hook-free.
  if (!props.always && !isHeldResolveRefusal(props.error)) return null;
  return <DiscardButton {...props} />;
}

function DiscardButton({
  cycleId,
  error,
  then,
  testid = "held-resolve-discard",
  onDiscarded,
}: HeldResolveDiscardProps) {
  const discard = useDiscardHeldResolve(cycleId);
  const [done, setDone] = useState<string | null>(null);
  // A new refusal (another press of the cover after the discard) starts over.
  const errorKey = error instanceof ApiError ? error.detail : String(error ?? "");
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on a new error only
  useEffect(() => {
    setDone(null);
    discard.reset();
  }, [errorKey, cycleId]);

  if (done) {
    return (
      <output className="block text-xs text-fg-muted" data-testid={`${testid}-done`}>
        {done}
      </output>
    );
  }
  const failed = discard.error;
  const press = () => {
    discard.mutate(undefined, {
      onSuccess: (result) => {
        const line = heldResolveDiscardedLine(result, then);
        setDone(line);
        onDiscarded?.(line);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 404) {
          setDone(NOTHING_HELD_LINE);
          onDiscarded?.(NOTHING_HELD_LINE);
        }
      },
    });
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={discard.isPending}
        onClick={press}
        data-testid={`${testid}-button`}
      >
        {discard.isPending ? "Discarding…" : DISCARD_HELD_RESOLVE_LABEL}
      </Button>
      {failed && !(failed instanceof ApiError && failed.status === 404) ? (
        <span role="alert" className="text-xs text-danger" data-testid={`${testid}-error`}>
          {failed instanceof ApiError ? failed.detail : failed.message}
        </span>
      ) : null}
    </div>
  );
}
